<template>
  <!--
    WB-M1A -- the board editor: the Excalidraw 0.17.6 already in High-line,
    driven entirely from outside (doc 25 §2.6). Its own chrome is hidden by
    three container selectors and every tool is set through the imperative
    API by Borna's toolbar.

    This component owns no network call and no persistence: it mounts the
    scene it is given, reports its scene version when that changes, and hands
    its current scene over on request. The Workspace and the `aiTutorBoard`
    store decide when anything is saved.

    M1A has no media (doc 23 WB-M1-D9, WB-M1-D10): the image tool is off, file
    drops and image pastes are stopped before the editor sees them, and any
    element of a type M1A does not persist is removed from the scene.

    WB-M1B S3 (doc 23 §19): the one image the board has is the equation, a
    picture this browser draws itself from a LaTeX source. The editor can
    insert one, change its source and say which one is selected
    (`insertEquation`, `updateEquation`, `getEquation`, the
    `equation-selection` event); it has no Equation UI of its own. Every other
    image is still removed, and a picture is never taken from a clipboard: a
    pasted equation is drawn again here from its source.

    WB-M2A S1 (doc 31 §9): the editor can render a SNAPSHOT of what is
    selected (or, with nothing selected, of the visible board) as a PNG,
    through the drawing library's own export (`renderSnapshot`). It reads
    the scene and draws; it sends nothing and changes nothing. What the
    picture shows is decided by `boardSnapshot.js`.

    Deliberately NOT the human-session `SessionView/Whiteboard.vue`: that one
    is socket-synchronised and is left untouched.
  -->
  <div
    class="ai-tutor-board-editor"
    :class="{
      'ai-tutor-board-editor--ready': ready,
      'ai-tutor-board-editor--read-only': readOnly,
    }"
    data-testid="ai-tutor-board-editor"
  >
    <div ref="mount" class="ai-tutor-board-editor__mount"></div>
  </div>
</template>

<script>
import * as ExcalidrawLib from '@excalidraw/excalidraw'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { throttle } from 'lodash'
import {
  acquireBoardFont,
  releaseBoardFont,
  BOARD_FONT_FAMILY_ID,
} from './boardFont'
import {
  isRuntimeEquationImage,
  runtimeEquationMetrics,
  sceneVersionOf,
} from '@/services/aiTutor/boardScene'
import {
  loadEquationEngine,
  validateEquationSource,
} from '@/services/aiTutor/boardEquation'
import { isPointInBox } from '@/services/aiTutor/boardEquationGeometry'
import { equationInsertPlacement } from '@/services/aiTutor/boardEquationPlacement'
import { isBoardContentOutOfView } from '@/services/aiTutor/boardView'
import {
  renderBoardSnapshot,
  resolveSnapshotScope,
} from '@/services/aiTutor/boardSnapshot'
import {
  NO_PRESSURE,
  isUsablePressure,
  normalizeBoardStrokes,
} from '@/services/aiTutor/boardStroke'
import { normalizePenStyle } from '@/services/aiTutor/boardPen'
import {
  createEquationPictures,
  equationInfo,
  equationInsertEm,
  equationSourceUpdate,
  newEquationImage,
  reconcileBoardElements,
  sanitizePastedElements,
  selectedEquationIdOf,
} from '@/services/aiTutor/boardEquationRuntime'

const CHROME_SELECTORS = [
  '.layer-ui__wrapper',
  '.FixedSideContainer',
  '.App-bottom-bar',
]

// WB-M1B S5 (doc 23 §27): the pointer events a pen stroke is drawn from.
const POINTER_EVENTS = ['pointerdown', 'pointermove', 'pointerup']

// P41 QA-27: the cursor over the round rotation handle above a selection
// (and while it turns): a circular arrow, ink on a white outline, its hot
// spot in the middle. Excalidraw 0.17.6 gives that handle `grab` -- the open
// hand it also uses for moving the whole board -- which said "drag the
// board", not "turn this". `pointer` stands in if the picture cannot load.
const ROTATE_CURSOR_SVG =
  "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>" +
  "<g fill='none' stroke-linecap='round' stroke-linejoin='round'>" +
  "<path d='M18.5 12a6.5 6.5 0 1 1-1.9-4.6' stroke='#fff' stroke-width='4.5'/>" +
  "<path d='M17 3.5v4.2h-4.2' stroke='#fff' stroke-width='4.5'/>" +
  "<path d='M18.5 12a6.5 6.5 0 1 1-1.9-4.6' stroke='#0b163f' stroke-width='2'/>" +
  "<path d='M17 3.5v4.2h-4.2' stroke='#0b163f' stroke-width='2'/>" +
  '</g></svg>'
export const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  ROTATE_CURSOR_SVG
)}") 12 12, pointer`

function hasFiles(dataTransfer) {
  if (!dataTransfer) return false
  if (dataTransfer.files && dataTransfer.files.length > 0) return true
  return Array.from(dataTransfer.types || []).indexOf('Files') !== -1
}

export default {
  name: 'BoardEditor',
  props: {
    locale: { type: String, default: 'fa' },
    // {elements, files, view} -- the decoded, persisted scene. Read once, when
    // the editor mounts; a different scene is a new editor (the Workspace
    // re-keys this component).
    initialScene: { type: Object, default: null },
    // A stale tab: the board is shown but cannot be edited.
    readOnly: { type: Boolean, default: false },
    // BT-53 (P41): the colour and width of what is drawn next ({color,
    // width}, `boardPen.js`). Applied when the editor comes up and whenever
    // it changes; null = the Board's default (INK, 2).
    penStyle: { type: Object, default: null },
  },
  data() {
    return {
      ready: false,
      // The id of the one selected equation, or null (also the payload of
      // the `equation-selection` event, emitted when it changes).
      selectedEquationId: null,
    }
  },
  watch: {
    readOnly() {
      this.renderEditor()
    },
    penStyle() {
      this.applyPenStyle()
    },
  },
  created() {
    // Kept off `data` on purpose: Vue must not walk the editor's API object
    // or the React root to make them reactive.
    this.api = null
    this.root = null
    this.destroyed = false
    this.fontHeld = false
    this.reconciling = false
    // The equation pictures this editor has drawn. Kept until it is
    // destroyed: undo and redo need the file of an earlier state.
    this.pictures = createEquationPictures()
    this.reportChange = throttle((elements, appState) => {
      if (this.destroyed) return
      this.$emit('change', this.describe(elements, appState))
    }, 120)
  },
  async mounted() {
    document.addEventListener('paste', this.onPaste, true)
    POINTER_EVENTS.forEach(type =>
      window.addEventListener(type, this.onPointer, true)
    )
    this.$el.addEventListener('drop', this.onDrop, true)
    this.$el.addEventListener('dragover', this.onDragOver, true)
    this.$el.addEventListener('dblclick', this.onDoubleClick, true)
    // P41 QA-27: a held Space moves the board -- its `grab` is Excalidraw's.
    window.addEventListener('keydown', this.onSpaceKey, true)
    window.addEventListener('keyup', this.onSpaceKey, true)
    window.addEventListener('blur', this.onSpaceKey)
    // Doc 25 §1.4 condition 1: the font alias must be able to draw BEFORE the
    // editor mounts.
    this.fontHeld = true
    await acquireBoardFont()
    if (this.destroyed) return
    this.renderEditor()
  },
  beforeDestroy() {
    this.destroyed = true
    this.reportChange.cancel()
    document.removeEventListener('paste', this.onPaste, true)
    POINTER_EVENTS.forEach(type =>
      window.removeEventListener(type, this.onPointer, true)
    )
    this.$el.removeEventListener('drop', this.onDrop, true)
    this.$el.removeEventListener('dragover', this.onDragOver, true)
    this.$el.removeEventListener('dblclick', this.onDoubleClick, true)
    window.removeEventListener('keydown', this.onSpaceKey, true)
    window.removeEventListener('keyup', this.onSpaceKey, true)
    window.removeEventListener('blur', this.onSpaceKey)
    if (this.cursorObserver) {
      this.cursorObserver.disconnect()
      this.cursorObserver = null
    }
    this.pictures.clear()
    if (this.root) {
      this.root.unmount()
      this.root = null
    }
    this.api = null
    if (this.fontHeld) releaseBoardFont()
  },
  methods: {
    describe(elements, appState) {
      const selected = (appState && appState.selectedElementIds) || {}
      const live = (elements || []).filter(element => !element.isDeleted)
      return {
        selectedEquationId: selectedEquationIdOf(live, selected),
        // Excalidraw's own scene version (the sum of the element versions):
        // the dirty signal of the autosave.
        sceneVersion: sceneVersionOf(elements),
        elementCount: live.length,
        selectedCount: live.filter(element => selected[element.id]).length,
        activeTool:
          (appState && appState.activeTool && appState.activeTool.type) ||
          'selection',
      }
    },
    initialData() {
      const scene = this.initialScene || {}
      const view = scene.view || {}
      const elements = ExcalidrawLib.restoreElements(scene.elements || [], null)
      // The files the scene came with are the pictures the codec drew for it
      // (`rehydrateBoardFiles`): this editor's own, not drawn a second time.
      const files = scene.files || {}
      const pen = normalizePenStyle(this.penStyle)
      elements.forEach(element => {
        if (!isRuntimeEquationImage(element) || !files[element.fileId]) return
        this.pictures.seed(element.customData.borna.latex, {
          fileId: element.fileId,
          metrics: runtimeEquationMetrics(element.fileId),
          file: files[element.fileId],
        })
      })
      return {
        elements,
        files: scene.files || {},
        appState: {
          // The dotted paper is drawn by CSS behind a transparent canvas.
          viewBackgroundColor: 'transparent',
          // Doc 25 §1.4 condition 2.
          currentItemFontFamily: BOARD_FONT_FAMILY_ID,
          currentItemRoughness: 0,
          // BT-53: the pen settings (default INK, 2 -- unchanged).
          currentItemStrokeWidth: pen.width,
          currentItemStrokeColor: pen.color,
          scrollX: typeof view.scrollX === 'number' ? view.scrollX : 0,
          scrollY: typeof view.scrollY === 'number' ? view.scrollY : 0,
          zoom: { value: typeof view.zoom === 'number' ? view.zoom : 1 },
        },
      }
    },
    // Called at mount and again when `readOnly` changes: React reconciles the
    // same root, `initialData` is only read the first time.
    renderEditor() {
      if (this.destroyed || !this.$refs.mount) return
      if (!this.root) {
        this.root = createRoot(this.$refs.mount)
        this.cachedInitialData = this.initialData()
      }
      const editor = React.createElement(ExcalidrawLib.Excalidraw, {
        excalidrawAPI: api => {
          if (this.api === api) return
          this.api = api
          this.onApiReady()
        },
        langCode: this.locale === 'fa' ? 'fa-IR' : 'en',
        initialData: this.cachedInitialData,
        viewModeEnabled: this.readOnly,
        UIOptions: {
          canvasActions: {
            loadScene: false,
            saveToActiveFile: false,
            saveAsImage: false,
            export: false,
            toggleTheme: false,
            clearCanvas: false,
            changeViewBackgroundColor: false,
          },
          // No media in M1A.
          tools: { image: false },
        },
        handleKeyboardGlobally: false,
        autoFocus: false,
        onChange: (elements, appState, files) =>
          this.onChange(elements, appState, files),
        // Runs before the editor takes what was pasted.
        onPaste: data => this.sanitizePaste(data),
      })
      this.root.render(
        React.createElement(
          'div',
          { style: { width: '100%', height: '100%' } },
          editor
        )
      )
    },
    onApiReady() {
      if (this.destroyed || !this.api) return
      // Excalidraw hands the API over while it is still mounting; a tool set
      // at that moment is dropped. The board opens on the pen once it is up.
      setTimeout(() => {
        if (this.destroyed || !this.api) return
        if (!this.readOnly) this.api.setActiveTool({ type: 'freedraw' })
        // BT-53: a setting chosen while the editor was coming up.
        this.applyPenStyle()
        // QA-27: the rotation handle's cursor.
        this.watchCursor()
        this.showContent()
        this.ready = true
        this.$emit(
          'ready',
          this.describe(
            this.api.getSceneElementsIncludingDeleted(),
            this.api.getAppState()
          )
        )
      }, 150)
      // Doc 25 §2.6: fail loudly if Excalidraw's own chrome is on screen.
      setTimeout(() => this.checkChrome(), 750)
    },
    onChange(elements, appState, files) {
      if (this.destroyed || !this.ready) return
      if (this.reconcile(elements, appState, files)) return
      this.reportSelection(elements, appState)
      this.reportChange(elements, appState)
    },
    // The board persists a fixed set of element types, and (WB-M1B) the
    // equation. Anything else that reaches the scene (an image from a route
    // this component could not close) is removed again, and an equation's
    // picture is always this browser's own, so the board on screen is the
    // board that is saved. Returns whether the scene was replaced (the
    // editor then reports again).
    reconcile(elements, appState, files) {
      if (this.reconciling || !this.api) return false
      const result = reconcileBoardElements(elements, {
        files,
        pictureOf: source => this.pictures.get(source),
        apply: ExcalidrawLib.newElementWith,
        // A resize in progress is not a new scale yet.
        settled: !(appState && appState.isResizing),
      })
      // A finished pen stroke whose device reported a pressure outside 0..1
      // (WB-M1B S5, `boardStroke.js`).
      const strokes = normalizeBoardStrokes(
        result.elements,
        ExcalidrawLib.newElementWith
      )
      const changed = result.changed || strokes.changed
      if (!changed && result.files.length === 0) return false
      this.reconciling = true
      try {
        if (changed) {
          this.api.updateScene({
            elements: strokes.elements,
            commitToHistory: false,
          })
        }
        // Handing a file back changes no element, so on its own it never
        // makes the board dirty.
        if (result.files.length > 0) this.api.addFiles(result.files)
      } finally {
        this.reconciling = false
      }
      return changed
    },
    // WB-M1B S5 (doc 23 §27): some phones report a pointer pressure far
    // outside 0..1 (about 11.5 on the phone of the S5 device run). Excalidraw
    // draws a pen stroke's width from that number while the finger moves. A
    // pressure that is not a measurement is reported as "this device does
    // not measure pressure" (0.5), which Excalidraw then simulates from the
    // speed of the stroke. Before the editor's own listeners (capture, on
    // the window); only for a pointer on the board.
    // P40 QA-42: a FINGER is not a pressure measurement either. Phones report
    // a constant in-range value for a touch (often 1), which Excalidraw took
    // as a real pressure and drew wider than a mouse stroke. A touch is
    // treated as "no pressure" too, so a finger stroke is drawn like a mouse
    // stroke; a stylus (pointerType 'pen') keeps its real pressure.
    onPointer(event) {
      const finger = event.pointerType === 'touch'
      if (
        finger
          ? event.pressure === NO_PRESSURE
          : isUsablePressure(event.pressure)
      ) {
        return
      }
      if (!this.$el || !this.$el.contains(event.target)) return
      try {
        Object.defineProperty(event, 'pressure', {
          value: NO_PRESSURE,
          configurable: true,
        })
      } catch (err) {} // eslint-disable-line no-empty
    },
    // Gives the editor the pictures of equations that are ALREADY in its
    // scene. The order matters: Excalidraw loads a file, and repaints the
    // elements showing it, for the elements its scene has at that moment; an
    // element added while its file is still loading keeps the grey
    // placeholder it was first painted with. A file the editor already has
    // is left alone (handing it over again would reload it for nothing).
    showPictures(files) {
      const present = this.api.getFiles()
      const missing = files.filter(
        file => !present[file.id] || present[file.id].dataURL !== file.dataURL
      )
      if (missing.length > 0) this.api.addFiles(missing)
    },
    reportSelection(elements, appState) {
      const id = selectedEquationIdOf(
        elements,
        appState && appState.selectedElementIds
      )
      if (id === this.selectedEquationId) return
      this.selectedEquationId = id
      this.$emit('equation-selection', id)
    },
    // Excalidraw's `onPaste`: called with what it read from the clipboard,
    // before it adds anything. The clipboard's files are never used, and
    // every pasted image is either drawn again here from its own equation
    // source or dropped (`sanitizePastedElements`). What Excalidraw is given
    // as the paste's files is only what this browser drew itself.
    async sanitizePaste(data) {
      if (!data || typeof data !== 'object') return true
      delete data.files
      if (!Array.isArray(data.elements)) return true
      if (data.elements.some(element => element && element.type === 'image')) {
        // A paste into a board that has no equation yet. Without the engine
        // nothing can be drawn and the pasted images are dropped.
        try {
          await loadEquationEngine()
        } catch (err) {} // eslint-disable-line no-empty
      }
      if (this.destroyed || !this.api || this.readOnly) return false
      const { elements, files } = sanitizePastedElements(
        data.elements,
        source => this.pictures.get(source)
      )
      if (elements.length === 0) return false
      data.elements = elements
      if (files.length > 0) {
        data.files = {}
        files.forEach(file => {
          data.files[file.id] = file
        })
      }
      return true
    },
    onPaste(event) {
      if (!hasFiles(event.clipboardData)) return
      const active = document.activeElement
      const inEditor =
        this.$el.contains(event.target) || (active && this.$el.contains(active))
      if (!inEditor) return
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    onDrop(event) {
      if (!hasFiles(event.dataTransfer)) return
      event.preventDefault()
      event.stopPropagation()
    },
    onDragOver(event) {
      if (!hasFiles(event.dataTransfer)) return
      event.preventDefault()
      event.stopPropagation()
    },
    // A double-click on the selected equation is a request to edit it
    // (`equation-edit`); Excalidraw would start a text at that point instead.
    // Everything else -- text, shapes, lines, the empty board, any tool but
    // selection -- is left to Excalidraw. Mouse only: Excalidraw's touch
    // double-tap does not go through this event.
    onDoubleClick(event) {
      if (this.destroyed || !this.ready || !this.api || this.readOnly) return
      const state = this.api.getAppState()
      if (!state.activeTool || state.activeTool.type !== 'selection') return
      const elements = this.api.getSceneElements()
      const id = selectedEquationIdOf(elements, state.selectedElementIds)
      if (!id) return
      const equation = elements.find(element => element.id === id)
      const point = ExcalidrawLib.viewportCoordsToSceneCoords(
        { clientX: event.clientX, clientY: event.clientY },
        state
      )
      if (!isPointInBox(point, equation)) return
      event.preventDefault()
      event.stopPropagation()
      this.$emit('equation-edit', id)
    },
    checkChrome() {
      if (this.destroyed || !this.$el) return
      const host = this.$el.querySelector('.excalidraw')
      if (!host) return
      const visible = Array.from(host.querySelectorAll('button')).filter(
        button => {
          const box = button.getBoundingClientRect()
          return box.width > 0 && box.height > 0
        }
      )
      const known = CHROME_SELECTORS.filter(selector =>
        host.querySelector(selector)
      )
      if (visible.length > 0 || known.length === 0) {
        // eslint-disable-next-line no-console
        console.error('[ai-tutor-board] Excalidraw chrome check failed', {
          visibleButtons: visible.length,
          containersFound: known,
        })
        this.$emit('chrome-leak', visible.length)
      }
    },
    /** @param {string} type -- an Excalidraw tool type */
    setTool(type) {
      if (!this.api || this.readOnly) return
      this.api.setActiveTool({ type })
    },
    // BT-53 (P41): the pen settings become the editor's "current item" style
    // -- what the NEXT element is drawn with. Nothing on the board changes
    // (selected elements included), and it is not a step of undo.
    applyPenStyle() {
      if (this.destroyed || !this.api) return
      const pen = normalizePenStyle(this.penStyle)
      const state = this.api.getAppState()
      if (
        state.currentItemStrokeColor === pen.color &&
        state.currentItemStrokeWidth === pen.width
      ) {
        return
      }
      this.api.updateScene({
        appState: {
          currentItemStrokeColor: pen.color,
          currentItemStrokeWidth: pen.width,
        },
      })
    },
    // P41 QA-27: Excalidraw writes its cursor onto its canvases' style. The
    // one `grab` it writes for the round rotation handle (selection tool,
    // something selected, no Space held) -- and its `grabbing` while a
    // selection turns -- become ROTATE_CURSOR. Every other cursor (resize
    // arrows, move, crosshair, the Space-drag hand) is Excalidraw's own,
    // unchanged; the handle itself and rotating are not touched.
    watchCursor() {
      const host = this.$el && this.$el.querySelector('.excalidraw')
      if (
        !host ||
        this.cursorObserver ||
        typeof MutationObserver !== 'function'
      )
        return
      this.cursorObserver = new MutationObserver(records => {
        records.forEach(record => this.fixCursor(record.target))
      })
      this.cursorObserver.observe(host, {
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      })
    },
    fixCursor(target) {
      if (!target || target.tagName !== 'CANVAS' || !this.api) return
      const cursor = target.style.cursor
      if (cursor !== 'grab' && cursor !== 'grabbing') return
      const state = this.api.getAppState()
      const rotating = !!state.isRotating
      if (cursor === 'grabbing' && !rotating) return
      if (cursor === 'grab' && !rotating) {
        if (this.spaceHeld) return
        if (!state.activeTool || state.activeTool.type !== 'selection') return
        const selected = state.selectedElementIds || {}
        if (!Object.keys(selected).some(id => selected[id])) return
      }
      target.style.cursor = ROTATE_CURSOR
    },
    onSpaceKey(event) {
      if (!event || event.type === 'blur') {
        this.spaceHeld = false
        return
      }
      if (event.key !== ' ' && event.code !== 'Space') return
      this.spaceHeld = event.type === 'keydown'
    },
    /** @returns {{color: string, width: number}} what the next stroke uses */
    getPenStyle() {
      if (!this.api) return normalizePenStyle(this.penStyle)
      const state = this.api.getAppState()
      return {
        color: state.currentItemStrokeColor,
        width: state.currentItemStrokeWidth,
      }
    },
    // BT-54 (P41): everything on the board is removed in ONE step that the
    // editor's own undo takes back. Each element is marked deleted (a new
    // version of it), never dropped: the history keeps the elements, and the
    // scene version (the sum of the versions, deleted ones included) moves,
    // so the autosave sees the change and saves the now empty board. An
    // `elements: []` would neither be undoable nor reliably seen by the
    // autosave (an empty board's version can equal an earlier one).
    // Returns whether anything was cleared.
    clearBoard() {
      if (this.destroyed || !this.ready || !this.api || this.readOnly) {
        return false
      }
      const elements = this.api.getSceneElementsIncludingDeleted()
      if (!elements.some(element => !element.isDeleted)) return false
      this.api.updateScene({
        elements: elements.map(element =>
          element.isDeleted
            ? element
            : ExcalidrawLib.newElementWith(element, { isDeleted: true })
        ),
        appState: {
          selectedElementIds: {},
          selectedGroupIds: {},
          editingGroupId: null,
        },
        commitToHistory: true,
      })
      return true
    },
    // The imperative API has no undo; a script-dispatched shortcut on the
    // editor's own container measured 20/20 on desktop and mobile, chrome
    // hidden (doc 25 §2.2). The modifier mirrors Excalidraw's platform test.
    history(redo) {
      if (this.readOnly) return
      const host = this.$el && this.$el.querySelector('.excalidraw')
      if (!host) return
      const mac = /Mac|iPod|iPhone|iPad/.test(window.navigator.platform)
      host.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'z',
          code: 'KeyZ',
          ctrlKey: !mac,
          metaKey: mac,
          shiftKey: !!redo,
          bubbles: true,
          cancelable: true,
        })
      )
    },
    undo() {
      this.history(false)
    },
    redo() {
      this.history(true)
    },
    // Brings the content back into view (or the origin at 100% on an empty
    // board). View only: it never changes the scene.
    resetView() {
      if (!this.api) return
      const elements = this.api.getSceneElements()
      if (elements.length > 0) {
        this.api.scrollToContent(elements, {
          fitToContent: true,
          animate: false,
        })
      } else {
        this.api.updateScene({
          appState: { scrollX: 0, scrollY: 0, zoom: { value: 1 } },
        })
      }
    },
    // WB-M1B S6 (doc 23 §29, `boardView.js`): a board opens in the view it
    // was left in. When that view shows NOTHING of a board that has content
    // (it was left on a wider screen), the content is brought into view the
    // way "reset view" does. Before the editor reports ready: it is how the
    // board opens, not a change.
    showContent() {
      const state = this.api.getAppState()
      if (!(state.width > 0) || !(state.height > 0)) return
      const elements = this.api.getSceneElements()
      if (isBoardContentOutOfView(elements, this.visibleBoard(state))) {
        this.resetView()
      }
    },
    // Hands the keyboard to the board (Delete, the arrow keys act on what is
    // selected) without scrolling the page. Changes nothing on the board.
    focus() {
      const host = this.$el && this.$el.querySelector('.excalidraw')
      if (host) host.focus({ preventScroll: true })
    },
    canEditEquations() {
      return !this.destroyed && this.ready && !!this.api && !this.readOnly
    },
    // The visible board in scene coordinates, with the editor's zoom.
    visibleBoard(state) {
      const zoom = state.zoom && state.zoom.value > 0 ? state.zoom.value : 1
      const corner = ExcalidrawLib.viewportCoordsToSceneCoords(
        { clientX: state.offsetLeft, clientY: state.offsetTop },
        state
      )
      return {
        x: corner.x,
        y: corner.y,
        width: state.width / zoom,
        height: state.height / zoom,
        zoom,
      }
    },
    // The centres of the equations that are on the board.
    equationCentres() {
      return this.api
        .getSceneElements()
        .filter(isRuntimeEquationImage)
        .map(element => ({
          x: element.x + element.width / 2,
          y: element.y + element.height / 2,
        }))
    },
    /**
     * Adds an equation to the board: one image, drawn here from `latex`,
     * selected, as one undoable step. The board becomes dirty like after any
     * other edit; what is saved is the equation's text carrier.
     * @param {{latex: string, em?: number,
     *   scenePosition?: {x: number, y: number}}} equation -- `em`: the
     *   scale (default: board-text size, less for a formula that would not
     *   fit the visible board); `scenePosition`: the scene point the
     *   equation is centred on (default: the middle of the visible board, a
     *   step further when an equation is already there) -- WB-M1B S5,
     *   `boardEquationPlacement.js`
     * @returns {Promise<{id: string, latex: string, em: number}|null>} null
     *   when the editor cannot be edited now (not ready, read-only)
     * @throws {BoardEquationError} a refused or invalid source, or the engine
     *   could not be loaded; typed, never carrying the source
     */
    async insertEquation({ latex, em, scenePosition } = {}) {
      if (!this.canEditEquations()) return null
      // A refused source never causes the engine to be downloaded.
      validateEquationSource(latex)
      await loadEquationEngine()
      if (!this.canEditEquations()) return null
      const picture = this.pictures.get(latex)
      // A caller that names the place gets exactly that place and scale.
      const placed =
        scenePosition &&
        Number.isFinite(scenePosition.x) &&
        Number.isFinite(scenePosition.y)
          ? {
              em: equationInsertEm(em),
              center: { x: scenePosition.x, y: scenePosition.y },
            }
          : equationInsertPlacement({
              natural: picture.metrics,
              em,
              visible: this.visibleBoard(this.api.getAppState()),
              taken: this.equationCentres(),
            })
      const image = ExcalidrawLib.restoreElements(
        [
          newEquationImage({
            latex,
            em: placed.em,
            center: placed.center,
            picture,
          }),
        ],
        null
      )[0]
      this.api.setActiveTool({ type: 'selection' })
      this.api.updateScene({
        elements: this.api.getSceneElementsIncludingDeleted().concat(image),
        appState: {
          selectedElementIds: { [image.id]: true },
          selectedGroupIds: {},
          editingGroupId: null,
        },
        commitToHistory: true,
      })
      this.showPictures([picture.file])
      return equationInfo(image)
    },
    /**
     * Changes the source of an equation: the same element (id, scale, rotated
     * top-left anchor, angle, every other Borna key), a new picture, a new
     * element version, one undoable step. The same source changes nothing.
     * @param {{id: string, latex: string}} change
     * @returns {Promise<{id: string, latex: string, em: number}|null>} null
     *   when `id` is not an equation on this board or the editor cannot be
     *   edited now
     * @throws {BoardEquationError} as `insertEquation`
     */
    async updateEquation({ id, latex } = {}) {
      if (!this.canEditEquations()) return null
      validateEquationSource(latex)
      await loadEquationEngine()
      if (!this.canEditEquations()) return null
      const picture = this.pictures.get(latex)
      const elements = this.api.getSceneElementsIncludingDeleted()
      const current = elements.find(element => element.id === id)
      if (!current || !isRuntimeEquationImage(current)) return null
      if (current.customData.borna.latex === latex) return equationInfo(current)
      const next = ExcalidrawLib.newElementWith(
        current,
        equationSourceUpdate(current, latex, picture)
      )
      this.api.updateScene({
        elements: elements.map(element =>
          element === current ? next : element
        ),
        commitToHistory: true,
      })
      this.showPictures([picture.file])
      return equationInfo(next)
    },
    /**
     * What the Equation UI may know about an equation on this board.
     * @param {string} id -- an element id
     * @returns {{id: string, latex: string, em: number}|null} null when it is
     *   not an equation on this board
     */
    getEquation(id) {
      if (!this.api) return null
      const element = this.api
        .getSceneElements()
        .find(candidate => candidate.id === id)
      return element && isRuntimeEquationImage(element)
        ? equationInfo(element)
        : null
    },
    /**
     * WB-M2A S1 -- a picture of the selection (or, with nothing selected, of
     * the visible board), rendered here by the drawing library's own export
     * with the board's own equation pictures. Changes nothing on the board
     * and sends nothing. The elements, ids and bounds returned describe the
     * picture for the share record; the picture itself is the Blob.
     * @returns {Promise<{scope: 'selection'|'viewport', elementIds: string[],
     *   bounds: object, selectedCount: number, blob: Blob, width: number,
     *   height: number, bytes: number, scale: number, exportMs: number,
     *   sceneVersion: number}|null>} null when the editor is not up or
     *   nothing would be in the picture
     */
    async renderSnapshot() {
      if (this.destroyed || !this.ready || !this.api) return null
      const state = this.api.getAppState()
      const elements = this.api.getSceneElements()
      const scoped = resolveSnapshotScope({
        elements,
        selectedElementIds: state.selectedElementIds,
        visible: this.visibleBoard(state),
      })
      if (!scoped) return null
      const rendered = await renderBoardSnapshot({
        elements: scoped.elements,
        appState: state,
        files: this.api.getFiles(),
        exportToBlob: ExcalidrawLib.exportToBlob,
      })
      if (this.destroyed) return null
      return {
        scope: scoped.scope,
        elementIds: scoped.elementIds,
        bounds: scoped.bounds,
        selectedCount: scoped.selectedCount,
        blob: rendered.blob,
        width: rendered.width,
        height: rendered.height,
        bytes: rendered.bytes,
        scale: rendered.scale,
        exportMs: rendered.exportMs,
        sceneVersion: sceneVersionOf(
          this.api.getSceneElementsIncludingDeleted()
        ),
      }
    },
    /**
     * The scene as it is right now, for the codec: the live elements and the
     * editor's app state (of which only the view is ever persisted).
     * @returns {{elements: object[], appState: object}}
     */
    getScene() {
      if (!this.api) return { elements: [], appState: {} }
      return {
        elements: this.api.getSceneElements(),
        appState: this.api.getAppState(),
      }
    },
  },
}
</script>

<style lang="scss">
// Not scoped: Excalidraw renders its own DOM under the mount point. Every
// rule is prefixed with this component's root class.
.ai-tutor-board-editor {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  // The dotted paper.
  background-color: #fff;
  background-image: radial-gradient(
    circle,
    rgba(11, 22, 63, 0.14) 1px,
    transparent 1.2px
  );
  background-size: 22px 22px;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.ai-tutor-board-editor__mount {
  width: 100%;
  height: 100%;
}

// Doc 25 §2.4: three container selectors, 0 visible controls on desktop and
// mobile. High-line is pinned to Excalidraw 0.17.6.
.ai-tutor-board-editor .excalidraw .layer-ui__wrapper,
.ai-tutor-board-editor .excalidraw .FixedSideContainer,
.ai-tutor-board-editor .excalidraw .App-bottom-bar {
  display: none !important;
}

// WB-M1B S5 (doc 23 §27): Excalidraw's own context menu -- a right click, and
// on a touch screen a finger held on an element with the selection tool. It
// opens UNDER the finger, and lifting the finger picks the item that is
// there ("Add to library" on an equation: a full-screen error of
// Excalidraw's). It is not Borna's interface any more than the rest above.
// Excalidraw has no option to switch it off; it is closed again by the next
// press on the board, whether it is shown or not.
.ai-tutor-board-editor .excalidraw .popover {
  display: none !important;
}
</style>
